// Phase 1A Step 18 — usePhantomCycle hook.
//
// Reads the authenticated user's most recent OPEN Phantom slip_cycles row
// (mode='phantom', non-terminal status). Exposes a createCycle() helper
// that wraps the existing public.create_phantom_simulation_cycle() RPC and
// refetches afterwards.
//
// Trust contract:
//   - RLS limits SELECT to the caller's own slip_cycles rows. The hook
//     never queries other tables and never touches fixture_cache.
//   - Writes are NEVER direct: the RPC is the only path, so users still
//     cannot fabricate cycles client-side. Migration 0024 dropped the
//     authenticated INSERT/UPDATE policies on slip_cycles + rollover_chains.
//   - The selections JSON is what the RPC stored — every entry carries
//     source='manual_seed' and simulated=true.

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { useTableQuery } from "../data/useTableQuery.ts";

export type PhantomCycleRow = Database["public"]["Tables"]["slip_cycles"]["Row"];

// Mirrors the per-selection JSON shape that create_phantom_simulation_cycle
// writes. Every field is optional at the type level so we can defensively
// render whatever the RPC produced; the verify suite asserts the contract.
export interface PhantomSelection {
  fixture_id?: string;
  source?: string;
  league_name?: string | null;
  country_code?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  kickoff_at?: string | null;
  phantom_pick?: string;
  simulated?: boolean;
}

// Phantom cycle statuses the panel reads. Includes both open (recommend /
// confirm) and terminal (won / busted) states so the post-resolve view
// can render. Mirrors the idempotency clause in create_phantom_simulation_cycle
// plus Step 20's resolve_phantom_simulation_cycle terminal statuses.
const VISIBLE_STATUSES: readonly string[] = [
  "recommended",
  "phantom_recommended",
  "phantom_confirmed",
  "phantom_won",
  "phantom_busted",
];

export interface PhantomCycleState {
  cycle: PhantomCycleRow | null;
  loading: boolean;
  error: string | null;
  busy: boolean;
  refetch: () => Promise<void>;
  createCycle: () => Promise<{ error: string | null }>;
  confirmCycle: (cycleId: string) => Promise<{ error: string | null }>;
  resolveCycle: (cycleId: string) => Promise<{ error: string | null }>;
}

export function usePhantomCycle(): PhantomCycleState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const q = useTableQuery<PhantomCycleRow[]>(
    ["phantom-cycle", userId],
    (client) =>
      client
        .from("slip_cycles")
        .select("*")
        .eq("user_id", userId as string)
        .eq("mode", "phantom")
        .in("status", VISIBLE_STATUSES)
        .order("created_at", { ascending: false })
        .limit(1) as unknown as PromiseLike<{
        data: PhantomCycleRow[] | null;
        error: { message: string } | null;
      }>,
    { enabled: Boolean(userId) },
  );

  const refetch = useCallback(async (): Promise<void> => {
    setError(null);
    await q.refetch();
  }, [q]);

  const createCycle = useCallback(async (): Promise<{ error: string | null }> => {
    if (busy) return { error: null };
    setBusy(true);
    setError(null);
    const { error: rpcError } = await supabase.rpc("create_phantom_simulation_cycle");
    if (rpcError) {
      setError(rpcError.message);
      setBusy(false);
      return { error: rpcError.message };
    }
    await q.refetch();
    setBusy(false);
    return { error: null };
  }, [busy, supabase, q]);

  // Confirms an existing Phantom cycle. The RPC's idempotent path means a
  // double-tap is safe — it returns the existing confirmation. After the
  // call, refetch() pulls the new status so the panel re-renders.
  const confirmCycle = useCallback(
    async (cycleId: string): Promise<{ error: string | null }> => {
      if (busy) return { error: null };
      setBusy(true);
      setError(null);
      const { error: rpcError } = await supabase.rpc("confirm_phantom_simulation_cycle", {
        p_cycle_id: cycleId,
      });
      if (rpcError) {
        setError(rpcError.message);
        setBusy(false);
        return { error: rpcError.message };
      }
      await q.refetch();
      setBusy(false);
      return { error: null };
    },
    [busy, supabase, q],
  );

  // Resolves a confirmed Phantom cycle deterministically (Phase 1A: parity
  // rule + optional forced_outcome from fixture_cache.raw_data). Idempotent
  // server-side: re-call returns the existing terminal state. See
  // docs/phantom-resolution-source-of-truth.md.
  //
  // Phase 1A Step 31 / Phase 6D.1 — Win and bust paths both auto-cascade to
  // a sister chain-mutation RPC after resolve returns:
  //
  //   - status='phantom_won'  → public.progress_phantom_chain(p_cycle_id)
  //                              advances current_week + transitions
  //                              status (Step 31).
  //   - status='phantom_busted' → public.apply_bust_protocol(p_cycle_id)
  //                              applies chain.bust_protocol (stop /
  //                              restart / halve_and_continue) per
  //                              CLAUDE.md §7 (Phase 6D.1).
  //
  // Backend stays separated: the resolver itself NEVER updates
  // rollover_chains (verify-phantom-resolve asserts that); the
  // orchestration is purely client-side here. Both chain-mutation RPCs
  // are server-side idempotent — repeat calls return progressed:false /
  // applied:false, which the client treats as success. A chain-mutation
  // failure does NOT undo the resolved cycle; the cycle still shows as
  // terminal after refetch and the error surfaces via the hook's
  // existing error state.
  const resolveCycle = useCallback(
    async (cycleId: string): Promise<{ error: string | null }> => {
      if (busy) return { error: null };
      setBusy(true);
      setError(null);
      const { data: resolveData, error: rpcError } = await supabase.rpc(
        "resolve_phantom_simulation_cycle",
        { p_cycle_id: cycleId },
      );
      if (rpcError) {
        setError(rpcError.message);
        setBusy(false);
        return { error: rpcError.message };
      }
      // Auto-cascade branches: both server-side RPCs validate Phase-1A
      // simulated labels and reject anything outside their respective
      // status precondition.
      const resolveStatus = (resolveData as { status?: string } | null)?.status;
      let cascadeError: string | null = null;
      if (resolveStatus === "phantom_won") {
        const { error: progressError } = await supabase.rpc("progress_phantom_chain", {
          p_cycle_id: cycleId,
        });
        if (progressError) {
          cascadeError = progressError.message;
          setError(progressError.message);
        }
      } else if (resolveStatus === "phantom_busted") {
        const { error: bustError } = await supabase.rpc("apply_bust_protocol", {
          p_cycle_id: cycleId,
        });
        if (bustError) {
          cascadeError = bustError.message;
          setError(bustError.message);
        }
      }
      await q.refetch();
      setBusy(false);
      return { error: cascadeError };
    },
    [busy, supabase, q],
  );

  const cycle = userId ? (q.data?.[0] as PhantomCycleRow | undefined) ?? null : null;
  // Legacy: loading=true until the first read settles. With no user the legacy
  // refetch set loading=false immediately, so gate on userId.
  const loading = Boolean(userId) && q.isLoading;
  // Read error (from the query) OR the latest mutation error. A read failure
  // in the legacy hook nulled the cycle + set the message; the query throws on
  // error so q.data is undefined (cycle => null) and q.error carries it.
  const readError = q.error ? q.error.message : null;
  const combinedError = error ?? readError;

  return useMemo<PhantomCycleState>(
    () => ({
      cycle,
      loading,
      error: combinedError,
      busy,
      refetch,
      createCycle,
      confirmCycle,
      resolveCycle,
    }),
    [cycle, loading, combinedError, busy, refetch, createCycle, confirmCycle, resolveCycle],
  );
}
